import queryString from "query-string";
import {
  createHashRouter,
  createRoutesFromElements,
  Route,
} from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";

import {
  CompanyRedirectWrapper,
  CompanyScopeContextWrapper,
  DropdownSourceContextWrapper,
  ProtectedRoute,
  RoutingInterceptor,
  RoutingUnit,
} from "@ryde/controls";
import {
  AdditionsPage,
  AreasGroupsPage,
  AreasPage,
  BookingsPage,
  BusinessUnitsPage,
  ComplaintsPage,
  ControlsPage,
  CoursesPage,
  DataUploadsPage,
  DayGroupsPage,
  DayTypesPage,
  DisableHoursPage,
  DriversPage,
  DynamicDashboardPage,
  EmptyPage,
  FeedbackSolutionsPage,
  FeedbacksPage,
  FeedbackSubjectsPage,
  FlightsPage,
  FoodSuppliersPage,
  HelpTopicsPage,
  IncidentsPage,
  IncidentTypesPage,
  JourneysPage,
  LiveMapPage,
  LoginPage,
  McpConnectorActionPage,
  MealsPage,
  NotFoundPage,
  NotificationsPage,
  OauthConsentPage,
  PassengersPage,
  PassengerTypesPage,
  PriceRatesPage,
  ProduceButtonsPage,
  ProfilePage,
  ReasonCategoriesPage,
  ReasonsPage,
  ReportsPage,
  ReportsPageStart,
  ResetPasswordPage,
  RideProductionsPage,
  RidesPage,
  RideTypesPage,
  ShiftsPage,
  ShiftTemplatesPage,
  SitesPage,
  StationDistancesPage,
  StationRoutesPage,
  StationsPage,
  SupplierPriceRatesPage,
  SuppliersPage,
  SystemManagersPage,
  SystemParamsPage,
  SystemTagsPage,
  UnitsPage,
  UsersPage,
  VehiclesPage,
  VehicleTypesPage,
} from "@ryde/pages";
import {
  registerAppRoutes,
  REPORTS_INITIAL_PATH,
  RoutePaths,
} from "@ryde/shared";

import { SettingsWrapper } from "../wrappers/settings.wrapper";
import { AppServicesProvider } from "./app.services-provider";

const appRouteObjects = createRoutesFromElements(
  <>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    {/* OAuth consent for the MCP connector (RYDE-7833) — outside ProtectedRoute; owns its session probe. */}
    <Route path="/oauth/consent" element={<OauthConsentPage />} />
    {/* AI-connector action approval (RYDE-8447): outside ProtectedRoute; owns its session probe. */}
    <Route path="/mcp/action" element={<McpConnectorActionPage />} />
    <Route
      path="/"
      element={
        <ProtectedRoute>
          <QueryParamProvider
            adapter={ReactRouter6Adapter}
            options={{
              searchStringToObject: queryString.parse,
              objectToSearchString: queryString.stringify,
            }}
          >
            <CompanyScopeContextWrapper>
              <DropdownSourceContextWrapper>
                <AppServicesProvider />
                <RoutingInterceptor />
              </DropdownSourceContextWrapper>
            </CompanyScopeContextWrapper>
          </QueryParamProvider>
        </ProtectedRoute>
      }
    >
      <Route
        index
        element={
          <CompanyRedirectWrapper basePath="">
            <EmptyPage />
          </CompanyRedirectWrapper>
        }
      />

      <Route
        path="/c/:companyId/"
        element={
          <CompanyRedirectWrapper basePath="">
            <DynamicDashboardPage />
          </CompanyRedirectWrapper>
        }
      />

      <Route path="/additions/*" element={<RoutingUnit entity="addition" />} />

      <Route
        path="/c/:companyId/additions/*"
        element={
          <RoutingUnit entity="addition">
            <AdditionsPage />
          </RoutingUnit>
        }
      />

      <Route path="/areas/*" element={<RoutingUnit entity="area" />} />
      <Route
        path="/c/:companyId/areas/*"
        element={
          <RoutingUnit entity="area">
            <AreasPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/areas-groups/*"
        element={<RoutingUnit entity="areasGroup" />}
      />
      <Route
        path="/c/:companyId/areas-groups/*"
        element={
          <RoutingUnit entity="areasGroup">
            <AreasGroupsPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/business_units/*"
        element={<RoutingUnit entity="businessUnit" />}
      />
      <Route
        path="/c/:companyId/business_units/*"
        element={
          <RoutingUnit entity="businessUnit">
            <BusinessUnitsPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/complaints/*"
        element={<RoutingUnit entity="complaint" />}
      />
      <Route
        path="/c/:companyId/complaints/*"
        element={
          <RoutingUnit entity="complaint">
            <ComplaintsPage />
          </RoutingUnit>
        }
      />

      <Route path="/courses/*" element={<RoutingUnit entity="course" />} />
      <Route
        path="/c/:companyId/courses/*"
        element={
          <RoutingUnit entity="course">
            <CoursesPage />
          </RoutingUnit>
        }
      />

      <Route path="/drivers/*" element={<RoutingUnit entity="driver" />} />
      <Route
        path="/c/:companyId/drivers/*"
        element={
          <RoutingUnit entity="driver">
            <DriversPage />
          </RoutingUnit>
        }
      />

      <Route path="/feedbacks/*" element={<RoutingUnit entity="feedback" />} />
      <Route
        path="/c/:companyId/feedbacks/*"
        element={
          <RoutingUnit entity="feedback">
            <FeedbacksPage />
          </RoutingUnit>
        }
      />
      {/* SUBJECT */}
      <Route
        path="/feedback_subjects/*"
        element={<RoutingUnit entity="feedbackSubject" />}
      />
      <Route
        path="/c/:companyId/feedback_subjects/*"
        element={
          <RoutingUnit entity="feedbackSubject">
            <FeedbackSubjectsPage />
          </RoutingUnit>
        }
      />
      {/* SOLUTION */}
      <Route
        path="/feedback_solutions/*"
        element={<RoutingUnit entity="feedbackSolution" />}
      />
      <Route
        path="/c/:companyId/feedback_solutions/*"
        element={
          <RoutingUnit entity="feedbackSolution">
            <FeedbackSolutionsPage />
          </RoutingUnit>
        }
      />

      <Route path="/flights/*" element={<RoutingUnit entity="flight" />} />
      <Route
        path="/c/:companyId/flights/*"
        element={
          <RoutingUnit entity="flight">
            <FlightsPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/food_suppliers/*"
        element={<RoutingUnit entity="foodSupplier" />}
      />
      <Route
        path="/c/:companyId/food_suppliers/*"
        element={
          <RoutingUnit entity="foodSupplier">
            <FoodSuppliersPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/incident_types/*"
        element={<RoutingUnit entity="incidentType" />}
      />
      <Route
        path="/c/:companyId/incident_types/*"
        element={
          <RoutingUnit entity="incidentType">
            <IncidentTypesPage />
          </RoutingUnit>
        }
      />

      <Route path="/incidents/*" element={<RoutingUnit entity="incident" />} />
      <Route
        path="/c/:companyId/incidents/*"
        element={
          <RoutingUnit entity="incident">
            <IncidentsPage />
          </RoutingUnit>
        }
      />

      <Route path="/day_types/*" element={<RoutingUnit entity="dayType" />} />
      <Route
        path="/c/:companyId/day_types/*"
        element={
          <RoutingUnit entity="dayType">
            <DayTypesPage />
          </RoutingUnit>
        }
      />

      <Route path="/day_groups/*" element={<RoutingUnit entity="dayGroup" />} />
      <Route
        path="/c/:companyId/day_groups/*"
        element={
          <RoutingUnit entity="dayGroup">
            <DayGroupsPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/disable_hours/*"
        element={<RoutingUnit entity="disableHour" />}
      />
      <Route
        path="/c/:companyId/disable_hours/*"
        element={
          <RoutingUnit entity="disableHour">
            <DisableHoursPage />
          </RoutingUnit>
        }
      />
      <Route
        path="/notifications/*"
        element={<RoutingUnit entity="notification" />}
      />
      <Route
        path="/c/:companyId/notifications/*"
        element={
          <RoutingUnit entity="notification">
            <NotificationsPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/passengers/*"
        element={<RoutingUnit entity="passenger" />}
      />
      <Route
        path="/c/:companyId/passengers/*"
        element={
          <RoutingUnit entity="passenger">
            <PassengersPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/passenger_types/*"
        element={<RoutingUnit entity="passengerType" />}
      />
      <Route
        path="/c/:companyId/passenger_types/*"
        element={
          <RoutingUnit entity="passengerType">
            <PassengerTypesPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/price_rates/*"
        element={<RoutingUnit entity="priceRate" />}
      />
      <Route
        path="/c/:companyId/price_rates/*"
        element={
          <RoutingUnit entity="priceRate">
            <PriceRatesPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/supplier_price_rates/*"
        element={<RoutingUnit entity="supplierPriceRate" />}
      />
      <Route
        path="/c/:companyId/supplier_price_rates/*"
        element={
          <RoutingUnit entity="supplierPriceRate">
            <SupplierPriceRatesPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/data_uploads/*"
        element={<RoutingUnit entity="dataUpload" />}
      />
      <Route
        path="/c/:companyId/data_uploads/*"
        element={
          <RoutingUnit entity="dataUpload">
            <DataUploadsPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/produce_buttons/*"
        element={<RoutingUnit entity="produceButton" />}
      />
      <Route
        path="/c/:companyId/produce_buttons/*"
        element={
          <RoutingUnit entity="produceButton">
            <ProduceButtonsPage />
          </RoutingUnit>
        }
      />

      <Route path="/reasons/*" element={<RoutingUnit entity="reason" />} />
      <Route
        path="/c/:companyId/reasons/*"
        element={
          <RoutingUnit entity="reason">
            <ReasonsPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/reason_categories/*"
        element={<RoutingUnit entity="reasonCategory" />}
      />
      <Route
        path="/c/:companyId/reason_categories/*"
        element={
          <RoutingUnit entity="reasonCategory">
            <ReasonCategoriesPage />
          </RoutingUnit>
        }
      />

      <Route path="/bookings/*" element={<RoutingUnit entity="booking" />} />
      <Route
        path="/c/:companyId/bookings/*"
        element={
          <RoutingUnit entity="booking">
            <BookingsPage />
          </RoutingUnit>
        }
      />

      <Route path="/rides/*" element={<RoutingUnit entity="ride" />} />
      <Route
        path="/c/:companyId/rides/*"
        element={
          <RoutingUnit entity="ride">
            <RidesPage />
          </RoutingUnit>
        }
      />

      <Route path="/rides-map/*" element={<RoutingUnit entity="ride" />} />
      <Route
        path="/c/:companyId/rides-map/*"
        element={
          <RoutingUnit entity="ride">
            <LiveMapPage />
          </RoutingUnit>
        }
      />

      <Route path="/ride_types/*" element={<RoutingUnit entity="rideType" />} />
      <Route
        path="/c/:companyId/ride_types/*"
        element={
          <RoutingUnit entity="rideType">
            <RideTypesPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/ride_productions/*"
        element={<RoutingUnit entity="rideProduction" />}
      />
      <Route
        path="/c/:companyId/ride_productions/*"
        element={
          <RoutingUnit entity="rideProduction">
            <RideProductionsPage />
          </RoutingUnit>
        }
      />

      <Route path="/shifts/*" element={<RoutingUnit entity="shift" />} />
      <Route
        path="/c/:companyId/shifts/*"
        element={
          <RoutingUnit entity="shift">
            <ShiftsPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/shift_templates/*"
        element={<RoutingUnit entity="shiftTemplate" />}
      />
      <Route
        path="/c/:companyId/shift_templates/*"
        element={
          <RoutingUnit entity="shiftTemplate">
            <ShiftTemplatesPage />
          </RoutingUnit>
        }
      />

      <Route path="/sites/*" element={<RoutingUnit entity="site" />} />
      <Route
        path="/c/:companyId/sites/*"
        element={
          <RoutingUnit entity="site">
            <SitesPage />
          </RoutingUnit>
        }
      />

      <Route path="/stations/*" element={<RoutingUnit entity="station" />} />
      <Route
        path="/c/:companyId/stations/*"
        element={
          <RoutingUnit entity="station">
            <StationsPage />
          </RoutingUnit>
        }
      />

      <Route path="/suppliers/*" element={<RoutingUnit entity="supplier" />} />
      <Route
        path="/c/:companyId/suppliers/*"
        element={
          <RoutingUnit entity="supplier">
            <SuppliersPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/system_managers/*"
        element={<RoutingUnit entity="systemManager" />}
      />
      <Route
        path="/c/:companyId/system_managers/*"
        element={
          <RoutingUnit entity="systemManager">
            <SystemManagersPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/system_params/*"
        element={<RoutingUnit entity="systemParam" />}
      />
      <Route
        path="/c/:companyId/system_params/*"
        element={
          <RoutingUnit entity="systemParam">
            <SystemParamsPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/system_tags/*"
        element={<RoutingUnit entity="systemTag" />}
      />
      <Route
        path="/c/:companyId/system_tags/*"
        element={
          <RoutingUnit entity="systemTag">
            <SystemTagsPage />
          </RoutingUnit>
        }
      />

      <Route path="/units/*" element={<RoutingUnit entity="unit" />} />
      <Route
        path="/c/:companyId/units/*"
        element={
          <RoutingUnit entity="unit">
            <UnitsPage />
          </RoutingUnit>
        }
      />

      <Route path="/users/*" element={<RoutingUnit entity="user" />} />
      <Route
        path="/c/:companyId/users/*"
        element={
          <RoutingUnit entity="user">
            <UsersPage />
          </RoutingUnit>
        }
      />

      <Route path="/vehicles/*" element={<RoutingUnit entity="vehicle" />} />
      <Route
        path="/c/:companyId/vehicles/*"
        element={
          <RoutingUnit entity="vehicle">
            <VehiclesPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/vehicle_types/*"
        element={<RoutingUnit entity="vehicleType" />}
      />
      <Route
        path="/c/:companyId/vehicle_types/*"
        element={
          <RoutingUnit entity="vehicleType">
            <VehicleTypesPage />
          </RoutingUnit>
        }
      />

      <Route path="/meals/*" element={<RoutingUnit entity="meal" />} />
      <Route
        path="/c/:companyId/meals/*"
        element={
          <RoutingUnit entity="meal">
            <MealsPage />
          </RoutingUnit>
        }
      />

      <Route
        path="/station_distances/*"
        element={<RoutingUnit entity="stationDistance" />}
      />
      <Route
        path="/c/:companyId/station_distances/*"
        element={
          <RoutingUnit entity="stationDistance">
            <StationDistancesPage />
          </RoutingUnit>
        }
      />
      <Route
        path={`/${RoutePaths.STATION_ROUTES}/*`}
        element={<RoutingUnit entity="stationRoute" />}
      />
      <Route
        path={`/c/:companyId${RoutePaths.STATION_ROUTES}/*`}
        element={
          <RoutingUnit entity="stationRoute">
            <StationRoutesPage />
          </RoutingUnit>
        }
      />

      <Route path="/journeys/*" element={<RoutingUnit entity="journey" />} />
      <Route
        path="/c/:companyId/journeys/*"
        element={
          <RoutingUnit entity="journey">
            <JourneysPage />
          </RoutingUnit>
        }
      />
      <Route
        path="/profile/*"
        element={<CompanyRedirectWrapper basePath="profile" />}
      />
      <Route
        path="/c/:companyId/profile/*"
        element={
          <CompanyRedirectWrapper basePath="profile">
            <ProfilePage />
          </CompanyRedirectWrapper>
        }
      />

      <Route
        path="/settings/*"
        element={<CompanyRedirectWrapper basePath="settings" />}
      />
      <Route
        path="/c/:companyId/settings/*"
        element={
          <CompanyRedirectWrapper basePath="settings">
            <SettingsWrapper />
          </CompanyRedirectWrapper>
        }
      />

      <Route
        path="/reports/*"
        element={<CompanyRedirectWrapper basePath={REPORTS_INITIAL_PATH} />}
      />

      <Route
        path="/c/:companyId/reports/:id/*"
        element={
          <CompanyRedirectWrapper basePath="reports" isRoutingWithId={true}>
            <ReportsPage />
          </CompanyRedirectWrapper>
        }
      />
      <Route
        path="/c/:companyId/reports/*"
        element={
          <CompanyRedirectWrapper basePath={REPORTS_INITIAL_PATH}>
            <ReportsPageStart />
          </CompanyRedirectWrapper>
        }
      />
      <Route
        path="/controls"
        element={<CompanyRedirectWrapper basePath="controls" />}
      />
      <Route
        path="/c/:companyId/controls"
        element={
          <CompanyRedirectWrapper basePath="controls">
            <ControlsPage />
          </CompanyRedirectWrapper>
        }
      />
      <Route
        path="/helptopics/*"
        element={
          <CompanyRedirectWrapper basePath="helptopics" shouldKeepSubPath />
        }
      />
      <Route
        path="/c/:companyId/helptopics/*"
        element={
          <CompanyRedirectWrapper basePath="helptopics" shouldKeepSubPath>
            <HelpTopicsPage />
          </CompanyRedirectWrapper>
        }
      />

      <Route path="/pageNotFound" element={<NotFoundPage />} />
      <Route path="/c/:companyId/pageNotFound" element={<NotFoundPage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </>,
);

// Register routes so isValidAppPath() can match against them when redirecting
// unauthenticated users to /login?next=<path>.
registerAppRoutes(appRouteObjects);

export const appRouter = () => createHashRouter(appRouteObjects);
